import { useMemo, useRef, useState } from "react";
import { SignInButton, useAuth } from "@clerk/clerk-react";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import {
  createTemplateDocument,
  invitationTemplates,
} from "@/features/templates/catalog";
import { TemplatePreview } from "@/features/templates/TemplatePreview";
import { TemplateEnvelopePreview } from '@/features/templates/TemplateEnvelopePreview';
import { templateEnvelopeConfig } from '@/features/templates/templateEnvelope';
import { getEnvelopeCatalog } from '@/services/envelope-catalog';
import { createProject } from "@/services/projects";
import styles from "./DesignsPage.module.scss";
import { siteOrigin } from "@/lib/siteOrigin";

const route = getRouteApi("/product-shell/designs");
export function DesignsPage() {
  const { template } = route.useSearch();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();
  const [category, setCategory] = useState("All designs");
  const [query, setQuery] = useState("");
  const [face, setFace] = useState<"front" | "back" | "envelope">("front");
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [error, setError] = useState("");
  const chosen = invitationTemplates.find((t) => t.id === template);
  const documents = useMemo(
    () =>
      Object.fromEntries(
        invitationTemplates.map((t) => [t.id, createTemplateDocument(t)]),
      ),
    [],
  );
  const visible = invitationTemplates.filter(
    (t) =>
      (category === "All designs" || t.category === category) &&
      `${t.name} ${t.category} ${t.description}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  const select = async (id?: string) => {
    setFace("front");
    setError("");
    await navigate({ to: "/designs", search: { template: id } });
    window.scrollTo({ top: 0, behavior: "instant" });
  };
  async function useDesign() {
    if (!chosen || !isSignedIn || lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      const envelopeConfig = templateEnvelopeConfig(chosen, await getEnvelopeCatalog());
      const project = await createProject({
        name: `${chosen.name} invitation`,
        invitationDocument: createTemplateDocument(chosen),
        envelopeConfig,
      });
      await navigate({ to: "/editor/$projectId", params: { projectId: project.id } });
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Could not create invitation. Please try again.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <main className={styles.page}>
      {chosen ? (
        <>
          <button className={styles.back} onClick={() => void select()} disabled={busy}>
            ← All designs
          </button>
          <section className={styles.detail}>
            <div className={styles.previewPanel}>
              <div className={styles.largeCard}>
                {face === 'envelope' ? <TemplateEnvelopePreview template={chosen}/> : <TemplatePreview
                  blocks={
                    face === "front"
                      ? documents[chosen.id].mainCard.blocks
                      : documents[chosen.id].backCard!.blocks
                  }
                />}
              </div>
              <div className={styles.faceSwitch} aria-label="Card side">
                {(["front", "back", "envelope"] as const).map((side) => (
                  <button
                    key={side}
                    aria-pressed={face === side}
                    onClick={() => setFace(side)}
                  >
                    {side === "front" ? "Front" : side === 'back' ? "Back" : 'Envelope'}
                  </button>
                ))}
                <Link to="/envelope-demo" search={{ template: chosen.id }} className={styles.animationLink}>
                  Preview ↗
                </Link>
              </div>
            </div>
            <div className={styles.detailCopy}>
              <p className={styles.eyebrow}>{chosen.category} COLLECTION</p>
              <h1>{chosen.name}</h1>
              <p>{chosen.description}</p>
              <div className={styles.swatches}>
                {[chosen.paper, chosen.ink, chosen.accent].map((color) => (
                  <span key={color} style={{ background: color }} title={color} />
                ))}
              </div>
              <p>Paired envelope: <strong>{chosen.envelope.name}</strong></p>
              <h2>Made to become yours.</h2>
              <ul>
                <li>Editable front and back, in your existing editor</li>
                <li>Change wording, fonts, colors, and artwork</li>
                <li>Your own saved copy — the original stays untouched</li>
                <li>{chosen.envelope.name} envelope included — customize it anytime</li>
              </ul>
              <p className={styles.fonts}>Font pairing: {chosen.fonts}</p>
              {error && (
                <p role="alert" className={styles.error}>
                  {error}
                </p>
              )}
              {isSignedIn ? (
                <button
                  className={styles.primary}
                  disabled={busy}
                  onClick={() => void useDesign()}
                >
                  {busy ? "Creating your invitation…" : "Use this design"} ↗
                </button>
              ) : (
                <SignInButton
                  mode="modal"
                  forceRedirectUrl={`${siteOrigin()}/designs?template=${chosen.id}`}
                >
                  <button className={styles.primary} disabled={!isLoaded}>
                    Sign in to use this design ↗
                  </button>
                </SignInButton>
              )}
              <p className={styles.note}>
                Sample names, dates, and locations are placeholders. Update them before
                sharing.
              </p>
            </div>
          </section>
        </>
      ) : (
        <>
          <section className={styles.hero}>
            <p className={styles.eyebrow}>THE ARCHI COLLECTION</p>
            <h1>
              Begin with a design.
              <br />
              <em>Make it your own.</em>
            </h1>
            <p>
              From garden celebrations to timeless traditions.
              <br />
              Choose a ready-made invitation, then personalize every detail.
            </p>
            <Link to="/editor">Already started? Your invitations →</Link>
          </section>
          <div className={styles.filters}>
            <div aria-label="Design collections">
              {["All designs", "Floral", "Traditional", "Minimal", "Celebration"].map(
                (value) => (
                  <button
                    key={value}
                    aria-pressed={category === value}
                    onClick={() => setCategory(value)}
                  >
                    {value}
                  </button>
                ),
              )}
            </div>
            <input
              type="search"
              aria-label="Search designs"
              placeholder="Find your style…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <p className={styles.count}>
            {visible.length} ready-to-personalize sets · Front, back & envelope included
          </p>
          <section className={styles.grid} aria-label="Invitation designs">
            {visible.map((t) => (
              <article key={t.id}>
                <button
                  className={styles.artwork}
                  onClick={() => void select(t.id)}
                >
                  <span aria-hidden="true">
                    <TemplatePreview blocks={documents[t.id].mainCard.blocks} />
                  </span>
                  <span className={styles.previewLabel}>Explore design ↗</span>
                </button>
                <div className={styles.caption}>
                  <div>
                    <h2>{t.name}</h2>
                    <p>
                      {t.category} · {t.fonts.split(" + ")[0]}
                    </p>
                    <p><span aria-hidden="true" style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:t.envelope.color,border:'1px solid #0002',marginRight:6}}/>{t.envelope.name} envelope included</p>
                  </div>
                  <button
                    onClick={() => void select(t.id)}
                    aria-label={`View ${t.name}`}
                  >
                    <span aria-hidden="true">↗</span>
                  </button>
                </div>
              </article>
            ))}
          </section>
          {visible.length === 0 && (
            <p className={styles.empty}>
              No matching designs. Try another collection or search.
            </p>
          )}
          <p className={styles.note}>
            Original Archi illustrations. All text and ornaments remain separate,
            editable elements.
          </p>
        </>
      )}
    </main>
  );
}
